import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_ENDPOINT } from './backend.config';
import { Response } from './response.model';

export type ErrorHandler = <T>(error: HttpErrorResponse, caught: Observable<T>) => Observable<T>;

@Injectable({
    providedIn: 'root',
})
export class AutopropBackend {

    private headers: { [key: string]: string | string[] } = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    errorHandler: ErrorHandler = (error, caught) => throwError(() => error);

    constructor(@Inject(API_ENDPOINT) private apiUrl: string, private http: HttpClient) {}

    setHeaders(headers: { [key: string]: string | string[] }) {
        this.headers = {
            ...this.headers,
            ...headers,
        };
    }

    get<T extends Response = Response>(path: string, options?: Partial<{ ignoreLoadingBar: boolean }>) {
        options = {
            ignoreLoadingBar: false,
            ...options,
        };
        const headers = {
            ...this.headers,
        };
        if (options.ignoreLoadingBar) {
            headers['ignoreLoadingBar'] = '';
        }
        return this.http.get<T>(`${this.apiUrl}${path}`, { headers }).pipe(catchError(this.errorHandler.bind(this))) as Observable<T>;
    }

    post<T extends Response = Response>(path: string, body: unknown, options?: Partial<{ ignoreLoadingBar: boolean; bypassErrorHandler: boolean }>) {
        options = {
            ignoreLoadingBar: false,
            bypassErrorHandler: false,
            ...options,
        };
        const headers = { ...this.headers };
        if (options.ignoreLoadingBar) {
            headers['ignoreLoadingBar'] = '';
        }
        return this.http.post<T>(`${this.apiUrl}${path}`, JSON.stringify(body), { headers }).pipe(
            catchError((error, caught) => {
                if (!options?.bypassErrorHandler) {
                    return this.errorHandler(error, caught);
                }
                return throwError(() => error);
            })
        ) as Observable<T>;
    }

    postFile<T extends Response = Response>(path: string, formData: FormData, options?: Partial<{ ignoreLoadingBar: boolean }>) {
        options = {
            ignoreLoadingBar: false,
            ...options,
        };
        const headers = { ...this.headers };
        if (options.ignoreLoadingBar) {
            headers['ignoreLoadingBar'] = '';
        }
        delete headers['Content-Type'];

        return this.http.post<T>(`${this.apiUrl}${path}`, formData, { headers }).pipe(catchError(this.errorHandler.bind(this)));
    }

    postBlob(path: string, body: unknown) {
        return this.http.post(`${this.apiUrl}${path}`, JSON.stringify(body), { headers: this.headers, responseType: 'blob' });
    }

    postBlobWithResponse(path: string, body: unknown) {
        return this.http.post(`${this.apiUrl}${path}`, body, {
                headers: this.headers,
                observe: 'response',
                responseType: 'blob',
            }
        );
    }

    delete(path: string, options?: Partial<{ ignoreLoadingBar: boolean }>) {
        options = {
            ignoreLoadingBar: false,
            ...options,
        };
        const headers = { ...this.headers };
        if (options.ignoreLoadingBar) {
            headers['ignoreLoadingBar'] = '';
        }
        return this.http.delete<Response>(`${this.apiUrl}${path}`, { headers }).pipe(catchError(this.errorHandler.bind(this)));
    }
}
